import {
  isHttpUrl,
  tableObjectToMarkdown,
  type CanvasObject,
  type EmojiStampKind,
} from "@refinement-canvas/domain";
import { escapeHtml, markdownToClipboardHtml } from "./markdown-clipboard-html";

// キャンバス図形 1 件を「クリップボード用の Markdown 断片（text/plain）」と
// 「HTML 断片（text/html）」へ変換する純粋関数群。
//
// 目的は、他アプリ（Teams・Word・Connect Note など）へ貼り付けたときに、画像・リンク・
// テキスト・表がそれぞれ意味のある形で入ること。他アプリで表現できない種別（図形・ペン・
// コネクタ・コメントスレッド）は、どちらも空文字にして何も書かない。
// キャンバス内の復元は canvas-clipboard.ts がカスタム MIME と text/html の data 属性で行うため、
// 本モジュールの出力内容は復元結果に影響しない。

/** 出力しない種別・内容で使う空の断片。 */
const EMPTY_PARTS = { html: "", plain: "" } as const;

/** 動画は他アプリで再生できないため、公開 URL へのリンクとして出す。その表示文字。 */
const VIDEO_LINK_LABEL = "動画";

/**
 * スタンプ種別 → クリップボードへ出す絵文字グリフ。
 * stamp-object.tsx の STAMP_KIND_GLYPH と同じ値を持つが、lib のモジュールから
 * features/canvas/objects の React コンポーネントを import しないため複製している
 * （canvas-thumbnail.ts の STAMP_THUMB_GLYPH と同じ理由）。
 * 型は EmojiStampKind 基準にして、絵文字種別の増減にキー追従が必要になるようにする
 * （avatar は画像描画のため対象外）。
 */
export const STAMP_CLIPBOARD_GLYPH: Record<EmojiStampKind, string> = {
  "thumbs-up": "👍",
  heart: "❤️",
  party: "🎉",
  grin: "😄",
  surprised: "😮",
  pray: "🙏",
  eyes: "👀",
  bulb: "💡",
  check: "✅",
  rocket: "🚀",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
  cry: "😢",
  sob: "😭",
  disappointed: "😞",
  pensive: "😔",
  "broken-heart": "💔",
};

/** 未知の種別（新しいクライアントが増やした種別）を受け取ったときの代替グリフ。 */
export const UNKNOWN_STAMP_CLIPBOARD_GLYPH = "❔";

/** クリップボード用の断片（どちらも空文字になり得る＝その図形は出力しない）。 */
export interface CanvasObjectClipboardParts {
  readonly plain: string;
  readonly html: string;
}

/**
 * 図形の表示位置の昇順（y → x → id）で並べる比較関数。
 * 同じ座標に重なった図形でも結果が実行ごとに変わらないよう、最後に id を見る。
 * 並べ替えは text/plain・text/html の生成順にだけ使い、JSON ペイロードの順序は変えない。
 */
export function compareForClipboardOrder(a: CanvasObject, b: CanvasObject): number {
  if (a.y !== b.y) return a.y - b.y;
  if (a.x !== b.x) return a.x - b.x;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/** 図形 1 件をクリップボード用の Markdown / HTML 断片へ変換する。 */
export function objectToClipboardParts(object: CanvasObject): CanvasObjectClipboardParts {
  switch (object.type) {
    case "text":
      if (object.text.length === 0) return EMPTY_PARTS;
      return {
        html: markdownToClipboardHtml(object.text, object.inlineStyles),
        plain: object.text,
      };
    case "sticky":
      if (object.text.length === 0) return EMPTY_PARTS;
      // 付箋の本文は Markdown として解釈せず、他アプリへは文字列だけを渡す（設計の要求）。
      return {
        // 補間値は escapeHtml() 済みで、置換後に足すのは固定の <br> だけ。
        // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
        html: `<p>${escapeHtml(object.text).replace(/\r?\n/gu, "<br>")}</p>`,
        plain: object.text,
      };
    case "section":
      if (object.text.length === 0) return EMPTY_PARTS;
      // 補間値は escapeHtml() 済み。
      // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
      return { html: `<p><strong>${escapeHtml(object.text)}</strong></p>`, plain: object.text };
    case "table":
      return { html: tableObjectToHtml(object.cells), plain: tableObjectToMarkdown(object.cells) };
    case "image": {
      // 未解決の添付（blob: / data:）はコピー対象から除外済みだが、防御的に再確認する。
      if (!isHttpUrl(object.src)) return EMPTY_PARTS;
      const alt = object.alt ?? "";
      return {
        // src は直前の isHttpUrl() で http(s) に限定済み、alt とあわせて escapeHtml() 済み。
        // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
        html: `<img src="${escapeHtml(object.src)}" alt="${escapeHtml(alt)}">`,
        plain: `![${escapeMarkdownLabel(alt)}](${escapeMarkdownUrl(object.src)})`,
      };
    }
    case "video":
      if (!isHttpUrl(object.src)) return EMPTY_PARTS;
      return {
        // src は直前の isHttpUrl() で http(s) に限定済みで、ラベルとあわせて escapeHtml() 済み。
        // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
        html: `<a href="${escapeHtml(object.src)}">${escapeHtml(VIDEO_LINK_LABEL)}</a>`,
        plain: `[${VIDEO_LINK_LABEL}](${escapeMarkdownUrl(object.src)})`,
      };
    case "link-card": {
      const label = object.title.length > 0 ? object.title : object.url;
      if (!isHttpUrl(object.url)) {
        // javascript: などのスキームはリンクにせず、表示文字だけを残す。
        // 補間値は escapeHtml() 済み。
        // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
        return { html: `<span>${escapeHtml(label)}</span>`, plain: label };
      }
      return {
        // url は直前の isHttpUrl() で http(s) に限定済みで、ラベルとあわせて escapeHtml() 済み。
        // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
        html: `<a href="${escapeHtml(object.url)}">${escapeHtml(label)}</a>`,
        plain:
          object.title.length > 0
            ? `[${escapeMarkdownLabel(object.title)}](${escapeMarkdownUrl(object.url)})`
            : object.url,
      };
    }
    case "stamp": {
      // avatar は、貼り付け先で本人の顔写真が意図せず展開されるのを避けるため出力しない。
      if (object.stampKind === "avatar") return EMPTY_PARTS;
      const glyph = STAMP_CLIPBOARD_GLYPH[object.stampKind] ?? UNKNOWN_STAMP_CLIPBOARD_GLYPH;
      // glyph は固定表から引いた文字で、さらに escapeHtml() 済み。
      // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
      return { html: `<span>${escapeHtml(glyph)}</span>`, plain: glyph };
    }
    default:
      // shape / pen-stroke / connector / comment-thread は他アプリで表現できないため出力しない。
      // （comment-thread はそもそもクリップボード経由の復元対象にもなっていない。）
      return EMPTY_PARTS;
  }
}

/**
 * Markdown のリンクラベル・画像 alt へ入れる文字列を、記法として解釈されないようにする。
 *
 * 画像の `alt` とリンクカードの `title` は外部由来（OGP の取得結果など）で、`x](javascript:alert(1))`
 * のような文字列を含み得る。素のまま `![alt](src)` へ埋めると、貼り付け先の Markdown 解釈で
 * 元の安全な URL とは別のリンクが生まれるため、記法の境界になる文字を打ち消す。
 * 改行はインラインリンクを途中で終わらせるため、空白へ畳む。
 */
function escapeMarkdownLabel(value: string): string {
  return value.replace(/[\\[\]]/gu, "\\$&").replace(/\r?\n/gu, " ");
}

/**
 * Markdown のインラインリンクの URL 部へ入れる文字列を、記法を壊さない形へ整える。
 * 括弧・空白・山括弧だけをパーセントエンコードする（解決先の URL は変わらない）。
 */
function escapeMarkdownUrl(value: string): string {
  return value.replace(
    /[ ()<>]/gu,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`
  );
}

/**
 * 表図形を HTML の表へ変換する。セルは現行と同じく平文（エスケープ済み）のまま入れる
 * （既にリリース済みの貼り付け結果を動かさないため。インライン記法の装飾化は見送り事項）。
 */
function tableObjectToHtml(cells: readonly (readonly string[])[]): string {
  const header = cells[0] ?? [];
  // セル値はいずれも escapeHtml() 済みで、行・表の組み立てに入るのは固定のタグだけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  const headerHtml = header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("");
  const bodyHtml = cells
    .slice(1)
    // 行とセルのテンプレートは同じ行で 2 件一致する。どちらも補間値は escapeHtml() 済み。
    // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
    .join("");
  // 補間値は、この関数が直前で組み立てた行断片だけ。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  return `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
}
